import type { PublicScriptMeta, ScriptMeta } from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isUrl = (value: unknown): value is string | null => {
  if (value === null) return true;
  if (typeof value !== 'string') return false;
  try { return ['https:', 'http:'].includes(new URL(value).protocol); } catch { return false; }
};

/** Pick public fields explicitly so stored admin metadata never travels with them. */
export function toPublicScriptMeta(meta: ScriptMeta, questions: number): PublicScriptMeta {
  const talentName = typeof meta.talentName === 'string' ? meta.talentName.trim() : '';
  return {
    family: meta.family,
    ...(meta.talentNameClient === true && talentName ? { talentName, talentNameClient: true } : {}),
    hasShootView: meta.hasShootView,
    shootViewPublicUrl: meta.shootViewPublicUrl,
    counts: { questions, toCameraRows: meta.counts.toCameraRows,
      voiceoverRows: meta.counts.voiceoverRows, totalRows: meta.counts.totalRows },
    updatedAt: meta.updatedAt,
  };
}

export function parsePublicScriptMeta(value: unknown): PublicScriptMeta | undefined {
  if (!isRecord(value) || !isRecord(value.counts)) return undefined;
  const count = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
  const c = value.counts;
  if ((value.family !== 'brief' && value.family !== 'script')
    || typeof value.hasShootView !== 'boolean'
    || !isUrl(value.shootViewPublicUrl)
    || !count(c.questions) || !count(c.toCameraRows) || !count(c.voiceoverRows) || !count(c.totalRows)
    || (value.updatedAt !== null && typeof value.updatedAt !== 'string')) {
    return undefined;
  }
  return {
    family: value.family,
    ...(value.talentNameClient === true
      && typeof value.talentName === 'string' && value.talentName.trim()
      ? { talentName: value.talentName.trim(), talentNameClient: true } : {}),
    hasShootView: value.hasShootView, shootViewPublicUrl: value.shootViewPublicUrl,
    counts: { questions: c.questions, toCameraRows: c.toCameraRows, voiceoverRows: c.voiceoverRows, totalRows: c.totalRows },
    updatedAt: value.updatedAt,
  };
}

/** Validate stored metadata without ever carrying arbitrary upstream content. */
export function parseScriptMeta(value: unknown): ScriptMeta | undefined {
  const meta = parsePublicScriptMeta(value);
  if (!meta || !isRecord(value)
    || typeof value.templateKey !== 'string' || !value.templateKey.trim()
    || typeof value.slug !== 'string' || !value.slug.trim()
    || !isUrl(value.shootViewAdminUrl)
    || typeof value.fetchedAt !== 'number' || !Number.isFinite(value.fetchedAt) || value.fetchedAt < 0) {
    return undefined;
  }
  return { ...meta, templateKey: value.templateKey, slug: value.slug,
    talentName: typeof value.talentName === 'string' ? value.talentName.trim() || null : null,
    talentNameClient: value.talentNameClient === true,
    shootViewAdminUrl: value.shootViewAdminUrl, fetchedAt: value.fetchedAt };
}
